import { useEffect, useState } from 'react';

type useCarouselPropTypes = {
    autoScrollPauseDuration?: number;
    autoScrollSpeed?: number;
    count: number;
    transitionDuration: number;
    updateCanUseTransition: (bool: boolean) => void;
    width: number;
};

/**
 * - extendedItems
 * To simulate infinite scrolling in both directions, the items passed into Carousel
 * are "extended" on both sides.
 *   items:               |  1  |  2  |  3  |  4  |  5  |
 *   extendedItems: |  5* |  1  |  2  |  3  |  4  |  5  |  1* |
 *
 * - canScroll
 * canScroll is used to throttle scrolling. setTimeout inside the fn means we can't
 * use _.throttle or something similar
 *
 * - isInfiniteScrollComplete
 * When scrolling to the ends of the list, the carousel must eventually scroll back
 * to the "real" item location (5* -> 5 or 1* -> 1). Do NOT animate this.
 * Controlling isInfiniteScrollComplete with useState is too slow and results in the css
 * transition applying to the 5* -> 5 and 1* -> 1 scroll when scrolling quickly.
 *
 * - autoScrollTimeout
 * Pause auto scroll for some duration after any user interaction to allow users
 * read and engage with the current carousel item.
 */

let canScroll = true;
let isInfiniteScrollComplete = true;
let autoScrollTimeout = setTimeout(() => {});

const SCROLL_START_THRESHOLD_DISTANCE = 10; // min distance dragged before horiz scrolling begins (pixels)
const SCROLL_NEXT_THRESHOLD_FRACTION = 0.3; // min distance dragged before resulting in index update (%)

export const useCarousel = ({
    autoScrollPauseDuration,
    autoScrollSpeed,
    count,
    transitionDuration,
    updateCanUseTransition,
    width
}: useCarouselPropTypes) => {
    const [index, updateIndex] = useState(0);
    const [translateX, updateTranslateX] = useState(1 * width);
    const [canAutoScroll, updateCanAutoScroll] = useState(true);

    const pauseAutoScroll = () => {
        if (!autoScrollSpeed) {
            return;
        }
        clearTimeout(autoScrollTimeout);
        updateCanAutoScroll(false);

        autoScrollTimeout = setTimeout(() => {
            updateCanAutoScroll(true);
        }, autoScrollPauseDuration);
    };
    useEffect(() => {
        return () => {
            clearTimeout(autoScrollTimeout);
        };
    }, []);
    const handleAutoScroll = ({ index: _index, width: _width }: { index: number; width: number }) => {
        if (_index < count - 1) {
            updateIndex(_index + 1);
            updateTranslateX((_index + 2) * _width);
        } else {
            updateIndex(0);
            updateTranslateX((count + 1) * _width);

            setTimeout(() => {
                isInfiniteScrollComplete = false;
                updateTranslateX(_width);

                setTimeout(() => {
                    isInfiniteScrollComplete = true;
                }, 0);
            }, transitionDuration);
        }
    };
    const handleGoToItem = (_index: number) => {
        updateIndex(_index);
        updateTranslateX((_index + 1) * width);
        pauseAutoScroll();
    };
    const handlePrev = () => {
        if (!canScroll) {
            return;
        }
        canScroll = false;
        setTimeout(() => {
            canScroll = true;
        }, transitionDuration);

        if (index === 0) {
            pauseAutoScroll();
            updateIndex(count - 1);
            updateTranslateX(0);

            setTimeout(() => {
                // after scroll completion, block transition and scroll to the "real" position
                isInfiniteScrollComplete = false;
                updateTranslateX(count * width);

                setTimeout(() => {
                    isInfiniteScrollComplete = true;
                }, 0);
            }, transitionDuration);
        } else {
            handleGoToItem(index - 1);
        }
    };
    const handleNext = () => {
        if (!canScroll) {
            return;
        }
        canScroll = false;
        setTimeout(() => {
            canScroll = true;
        }, transitionDuration);

        if (index === count - 1) {
            pauseAutoScroll();
            updateIndex(0);
            updateTranslateX((count + 1) * width);

            setTimeout(() => {
                isInfiniteScrollComplete = false;
                updateTranslateX(width);

                setTimeout(() => {
                    isInfiniteScrollComplete = true;
                }, 0);
            }, transitionDuration);
        } else {
            handleGoToItem(index + 1);
        }
    };

    // the states and functions below are for touch scrolling (mweb only)
    const [touchPosStart, updateTouchPosStart] = useState({ x: -1, y: -1 });
    const [touchPos, updateTouchPos] = useState({ x: -1, y: -1 });
    const [carouselTranslateXStart, updateCarouselTranslateXStart] = useState(0);
    const [isVertScrolling, updateIsVertScrolling] = useState(false);

    const handleTouchStart = (e: React.TouchEvent) => {
        if (!width || !canScroll) {
            /**
             * To reduce jank, block scrolling by dragging...
             * - until width ref is attached
             * - when already in the process of scrolling
             */
            return;
        }
        const touch = e.touches[0];
        if (touch) {
            updateCanUseTransition(false);
            updateCarouselTranslateXStart(translateX);
            updateTouchPosStart({ x: touch.clientX, y: touch.clientY });
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (touch && !isVertScrolling) {
            if (
                Math.abs(touchPosStart.y - touch.clientY) > SCROLL_START_THRESHOLD_DISTANCE &&
                touchPos.x === -1 &&
                touchPos.y === -1
            ) {
                updateIsVertScrolling(true);
            } else if (
                Math.abs(touchPosStart.x - touch.clientX) > SCROLL_START_THRESHOLD_DISTANCE &&
                !isVertScrolling
            ) {
                updateTouchPos({ x: touch.clientX, y: touch.clientY });
                updateTranslateX(carouselTranslateXStart + (touchPosStart.x - touch.clientX));
            }
        }
    };

    const handleTouchEnd = () => {
        if (isVertScrolling) {
            updateIsVertScrolling(false);
        } else if (touchPos.x === -1 || touchPos.y === -1) {
            // do nothing - user did not drag
        } else {
            if (touchPos.x - touchPosStart.x > width * SCROLL_NEXT_THRESHOLD_FRACTION) {
                handlePrev();
            } else if (touchPosStart.x - touchPos.x > width * SCROLL_NEXT_THRESHOLD_FRACTION) {
                handleNext();
            } else {
                handleGoToItem(index);
            }
        }
        updateTouchPos({ x: -1, y: -1 });
        updateCanUseTransition(true);
    };

    return {
        canAutoScroll,
        handleAutoScroll,
        handleGoToItem,
        handleNext,
        handlePrev,
        handleTouchEnd,
        handleTouchMove,
        handleTouchStart,
        index,
        isInfiniteScrollComplete,
        translateX,
        updateTranslateX
    };
};
